import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getWhiteboard, updateWhiteboard, deleteWhiteboard, addObject, updateObject, deleteObject, Whiteboard, WhiteboardObject } from '../../services/whiteboardService';
import PageHeader from '../common/PageHeader';
import StickyNote from './StickyNote';
import './Whiteboard.css';

interface WhiteboardDetailProps {
  onToggleSidebar: () => void;
}

const STICKY_NOTE_COLORS = [
  { name: 'yellow', value: '#ffeb3b' },
  { name: 'pink', value: '#f48fb1' },
  { name: 'blue', value: '#90caf9' },
  { name: 'green', value: '#a5d6a7' },
  { name: 'orange', value: '#ffcc80' },
  { name: 'purple', value: '#ce93d8' },
];

const WhiteboardDetail: React.FC<WhiteboardDetailProps> = ({ onToggleSidebar }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [whiteboard, setWhiteboard] = useState<Whiteboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [boardName, setBoardName] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  
  // Pan/Zoom state
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [scale, setScale] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  
  // Pinch zoom state
  const [isPinching, setIsPinching] = useState(false);
  const [pinchStart, setPinchStart] = useState({
    distance: 0,
    scale: 1,
    panX: 0,
    panY: 0,
    centerX: 0,
    centerY: 0
  });
  
  const canvasRef = useRef<HTMLDivElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Load whiteboard
  useEffect(() => {
    const loadWhiteboard = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        const board = await getWhiteboard(parseInt(id));
        if (board) {
          setWhiteboard(board);
          setBoardName(board.name);
        } else {
          navigate('/whiteboard');
        }
      } catch (error) {
        console.error('Failed to load whiteboard:', error);
        navigate('/whiteboard');
      } finally {
        setIsLoading(false);
      }
    };

    loadWhiteboard();
  }, [id, navigate]);

  // Auto-save debounced
  const debouncedSave = useCallback((updates: { name?: string; objects?: WhiteboardObject[] }) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(async () => {
      if (!id) return;
      setIsSaving(true);
      try {
        const updated = await updateWhiteboard(parseInt(id), updates);
        setWhiteboard(updated);
      } catch (error) {
        console.error('Failed to save whiteboard:', error);
      } finally {
        setIsSaving(false);
      }
    }, 500);
  }, [id]);

  // Pan handlers - Mouse
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.whiteboard-sticky-note')) {
      return; // Don't pan if clicking on sticky note
    }
    setIsPanning(true);
    setPanStart({ x: e.clientX - panX, y: e.clientY - panY });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isPanning) return;
    setPanX(e.clientX - panStart.x);
    setPanY(e.clientY - panStart.y);
  }, [isPanning, panStart]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Helper function to calculate distance between two touches
  const getTouchDistance = (touch1: { clientX: number; clientY: number }, touch2: { clientX: number; clientY: number }): number => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Helper function to get center point between two touches
  const getTouchCenter = (touch1: { clientX: number; clientY: number }, touch2: { clientX: number; clientY: number }): { x: number; y: number } => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  };

  // Pan handlers - Touch
  const handleTouchStart = (e: React.TouchEvent) => {
    if ((e.target as HTMLElement).closest('.whiteboard-sticky-note')) {
      return; // Don't pan/zoom if touching sticky note
    }
    
    if (e.touches.length === 2) {
      // Pinch zoom gesture
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = getTouchDistance(touch1, touch2);
      const center = getTouchCenter(touch1, touch2);
      
      setIsPinching(true);
      setIsPanning(false);
      
      // Get canvas bounds to calculate center in canvas coordinates
      if (canvasRef.current) {
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const centerX = center.x - canvasRect.left;
        const centerY = center.y - canvasRect.top;
        
        setPinchStart({
          distance,
          scale,
          panX,
          panY,
          centerX,
          centerY
        });
      }
    } else if (e.touches.length === 1 && !isPinching) {
      // Single touch pan
      const touch = e.touches[0];
      setIsPanning(true);
      setIsPinching(false);
      setPanStart({ x: touch.clientX - panX, y: touch.clientY - panY });
    }
  };

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (e.touches.length === 2 && isPinching) {
      // Pinch zoom
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = getTouchDistance(touch1, touch2);
      const scaleRatio = currentDistance / pinchStart.distance;
      const newScale = Math.max(0.1, Math.min(3, pinchStart.scale * scaleRatio));
      
      // Calculate new pan to zoom towards the pinch center
      if (canvasRef.current) {
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const currentCenterX = pinchStart.centerX;
        const currentCenterY = pinchStart.centerY;
        
        // Calculate the point in canvas coordinates before zoom
        const canvasX = (currentCenterX - pinchStart.panX) / pinchStart.scale;
        const canvasY = (currentCenterY - pinchStart.panY) / pinchStart.scale;
        
        // Calculate new pan to keep the same point under the pinch center
        const newPanX = currentCenterX - canvasX * newScale;
        const newPanY = currentCenterY - canvasY * newScale;
        
        setScale(newScale);
        setPanX(newPanX);
        setPanY(newPanY);
      }
    } else if (e.touches.length === 1 && isPanning && !isPinching) {
      // Single touch pan
      e.preventDefault(); // Prevent page scroll
      const touch = e.touches[0];
      setPanX(touch.clientX - panStart.x);
      setPanY(touch.clientY - panStart.y);
    }
  }, [isPanning, isPinching, panStart, pinchStart]);

  const handleTouchEnd = useCallback(() => {
    setIsPanning(false);
    setIsPinching(false);
  }, []);

  useEffect(() => {
    if (isPanning || isPinching) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      document.addEventListener('touchcancel', handleTouchEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('touchcancel', handleTouchEnd);
      };
    }
  }, [isPanning, isPinching, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Zoom handler
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.1, Math.min(3, scale * delta));
    setScale(newScale);
  };

  // Add sticky note
  const handleAddStickyNote = async () => {
    if (!whiteboard || !id || !canvasRef.current) return;
    
    // Calculate position in canvas coordinates (accounting for pan and scale)
    // Place note at center of visible viewport
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const viewportCenterX = canvasRect.width / 2;
    const viewportCenterY = canvasRect.height / 2;
    
    // Convert viewport center to canvas coordinates
    const canvasX = (viewportCenterX - panX) / scale;
    const canvasY = (viewportCenterY - panY) / scale;
    
    const newNote: WhiteboardObject = {
      id: `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'sticky_note',
      x: Math.max(0, canvasX - 100), // Center the note (half of 200px width), ensure non-negative
      y: Math.max(0, canvasY - 100), // Center the note (half of 200px height), ensure non-negative
      width: 200,
      height: 200,
      color: STICKY_NOTE_COLORS[0].value,
      content: ''
    };

    // Optimistic update
    const updatedObjects = [...(whiteboard.objects || []), newNote];
    setWhiteboard({ ...whiteboard, objects: updatedObjects });

    try {
      const updated = await addObject(parseInt(id), newNote);
      setWhiteboard(updated);
    } catch (error) {
      console.error('Failed to add sticky note:', error);
      // Revert optimistic update
      setWhiteboard(whiteboard);
    }
    
    setShowMenu(false);
  };

  // Update sticky note
  const handleUpdateNote = async (noteId: string, updates: Partial<WhiteboardObject>) => {
    if (!whiteboard || !id) return;

    // Optimistic update
    const updatedObjects = (whiteboard.objects || []).map(obj =>
      obj.id === noteId ? { ...obj, ...updates } : obj
    );
    setWhiteboard({ ...whiteboard, objects: updatedObjects });

    try {
      const updated = await updateObject(parseInt(id), noteId, updates);
      setWhiteboard(updated);
    } catch (error) {
      console.error('Failed to update sticky note:', error);
      // Revert optimistic update
      setWhiteboard(whiteboard);
    }
  };

  // Delete sticky note
  const handleDeleteNote = async (noteId: string) => {
    if (!whiteboard || !id) return;

    // Optimistic update
    const updatedObjects = (whiteboard.objects || []).filter(obj => obj.id !== noteId);
    setWhiteboard({ ...whiteboard, objects: updatedObjects });

    try {
      const updated = await deleteObject(parseInt(id), noteId);
      setWhiteboard(updated);
    } catch (error) {
      console.error('Failed to delete sticky note:', error);
      // Revert optimistic update
      setWhiteboard(whiteboard);
    }
  };

  // Update board name
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBoardName(e.target.value);
  };

  const handleNameBlur = () => {
    setIsEditingName(false);
    if (whiteboard && boardName !== whiteboard.name) {
      debouncedSave({ name: boardName });
    }
  };

  // Delete board
  const handleDeleteBoard = async () => {
    setShowMenu(false);
    if (!id || !window.confirm('Are you sure you want to delete this board?')) return;

    try {
      await deleteWhiteboard(parseInt(id));
      navigate('/whiteboard');
    } catch (error) {
      console.error('Failed to delete board:', error);
      alert('Failed to delete board');
    }
  };

  // Zoom handlers
  const handleZoomIn = () => {
    setScale(Math.min(3, scale + 0.1));
    setShowMenu(false);
  };

  const handleZoomOut = () => {
    setScale(Math.max(0.1, scale - 0.1));
    setShowMenu(false);
  };

  const handleReset = () => {
    setPanX(0);
    setPanY(0);
    setScale(1);
    setShowMenu(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  if (isLoading) {
    return (
      <div className="whiteboard-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">/whiteboard</h1>}
        />
        <div className="whiteboard-loading">Loading...</div>
      </div>
    );
  }

  if (!whiteboard) {
    return null;
  }

  const stickyNotes = (whiteboard.objects || []).filter(obj => obj.type === 'sticky_note');

  const rightActions = (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        className="whiteboard-menu-btn"
        onClick={() => setShowMenu(!showMenu)}
        title="Menu"
      >
        ⋯
      </button>
      {showMenu && (
        <div className="whiteboard-menu-dropdown">
          <button
            className="whiteboard-menu-item"
            onClick={handleAddStickyNote}
          >
            Add Sticky Note
          </button>
          <div className="whiteboard-menu-divider"></div>
          <div className="whiteboard-menu-zoom-section">
            <div className="whiteboard-menu-zoom-controls">
              <button
                className="whiteboard-menu-zoom-btn"
                onClick={handleZoomOut}
                title="Zoom Out"
              >
                −
              </button>
              <span className="whiteboard-menu-zoom-display">{Math.round(scale * 100)}%</span>
              <button
                className="whiteboard-menu-zoom-btn"
                onClick={handleZoomIn}
                title="Zoom In"
              >
                +
              </button>
            </div>
            <button
              className="whiteboard-menu-item"
              onClick={handleReset}
            >
              Reset View
            </button>
          </div>
          <div className="whiteboard-menu-divider"></div>
          <button
            className="whiteboard-menu-item whiteboard-menu-item-danger"
            onClick={handleDeleteBoard}
          >
            Delete Board
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="whiteboard-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <div className="whiteboard-header-center">
            {isEditingName ? (
              <input
                type="text"
                className="whiteboard-name-input"
                value={boardName}
                onChange={handleNameChange}
                onBlur={handleNameBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleNameBlur();
                  }
                }}
                autoFocus
              />
            ) : (
              <h1
                className="page-header-center-title whiteboard-name-display"
                onClick={() => setIsEditingName(true)}
                style={{ cursor: 'pointer' }}
              >
                {boardName}
              </h1>
            )}
            {isSaving && <span className="whiteboard-saving-indicator">Saving...</span>}
          </div>
        }
        rightActions={rightActions}
      />
      <div
        ref={canvasRef}
        className="whiteboard-canvas"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onWheel={handleWheel}
        style={{
          cursor: isPanning ? 'grabbing' : 'grab',
          touchAction: 'none'
        }}
      >
        <div
          className="whiteboard-canvas-content"
          style={{
            transform: `translate(${panX}px, ${panY}px) scale(${scale})`,
            transformOrigin: '0 0'
          }}
        >
          {stickyNotes.map((note) => (
            <StickyNote
              key={note.id}
              note={note}
              onUpdate={handleUpdateNote}
              onDelete={handleDeleteNote}
              scale={scale}
              panX={panX}
              panY={panY}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WhiteboardDetail;

